// OSS-verified application, applicant side (arc 101 slice 07, #68).
// `docs/idea/billing.md` § "OSS-verified flow" has always promised "apply via
// dashboard"; until now the only path was emailing oss@developerz.ai, which is
// unauditable and invisible to the applicant. Two round-trips over `apiFetch`:
// read the account's LATEST application, POST a new one.
//
// The verdict never comes from here — staff decide through its REST twin
// (`lib/admin-overrides.ts`) and the server flips the tier. This module only
// ever writes `pending`.

import { createQuery } from '@tanstack/solid-query';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** Where an application sits. Only staff move it off `pending`. */
export type OssVerificationStatus = 'pending' | 'approved' | 'rejected';

/** One application — the snake_case shape `/v1/oss/verification` returns. */
export type OssVerificationRequest = {
  readonly id: string;
  readonly repo_full_name: string;
  readonly evidence: string;
  readonly status: OssVerificationStatus;
  /** Staff verdict note — the reason shown back on a rejection. */
  readonly reviewer_note: string | null;
  readonly submitted_at_iso: string;
  readonly updated_at_iso: string;
  readonly decided_at_iso: string | null;
};

/** `GET /v1/oss/verification` — the latest application, or null if never applied. */
export type OssVerificationView = { readonly request: OssVerificationRequest | null };

/** What an applicant submits. */
export type OssVerificationDraft = { readonly repo_full_name: string; readonly evidence: string };

// Bounds mirrored from `@developerz/domain` (`entities/oss-verification.ts`,
// `value-objects/task-envelope.ts`) rather than imported: they're a `zod`
// schema, and pulling zod into the bundle to reject a typo the server rejects
// anyway is a poor trade for a static SPA. Drift shows up as a 400 the panel
// already surfaces.
/** `owner/repo` — `RepoFullNameSchema`. */
export const REPO_FULL_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,99}\/[A-Za-z0-9._-]{1,100}$/;
/** `OSS_EVIDENCE_MIN_CHARS` / `OSS_EVIDENCE_MAX_CHARS`, measured after trim. */
export const EVIDENCE_MIN = 20;
export const EVIDENCE_MAX = 4000;

export const OSS_VERIFICATION_KEY = 'oss_verification';

/**
 * `<StatusPill/>` tone per status. Shared by the applicant panel and the admin
 * queue so one application never reads green on one screen and amber on the
 * other — the two surfaces describe the same row to different people.
 */
export const OSS_STATUS_TONE = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
} as const satisfies Record<OssVerificationStatus, string>;

/**
 * `<StatusPill/>` label per status, beside its tone for the same two surfaces.
 * Literal `t()` calls on purpose: the i18n coverage scan reads only literal
 * keys, and a template-built `oss_verify.status_${status}` slipped past it.
 */
export const OSS_STATUS_LABEL = {
  pending: () => t('oss_verify.status_pending'),
  approved: () => t('oss_verify.status_approved'),
  rejected: () => t('oss_verify.status_rejected'),
} as const satisfies Record<OssVerificationStatus, () => string>;

/** The account's latest OSS-verification application. Idle with no session. */
export function useOssVerification(token: string | null = SESSION_TOKEN) {
  return createQuery<OssVerificationView>(() => ({
    queryKey: [OSS_VERIFICATION_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<OssVerificationView>('/v1/oss/verification', token),
  }));
}

/**
 * Which field a draft fails on, or `null` when it passes — the same bounds the
 * route's Zod body enforces, so a typo costs no round-trip. Returns the FIELD,
 * not a message: the copy (and its locale key) belongs to the form.
 */
export function draftProblem(draft: OssVerificationDraft): 'repo' | 'evidence' | null {
  if (!REPO_FULL_NAME_RE.test(draft.repo_full_name.trim())) return 'repo';
  const evidence = draft.evidence.trim();
  if (evidence.length < EVIDENCE_MIN || evidence.length > EVIDENCE_MAX) return 'evidence';
  return null;
}

/**
 * Submit — or replace — the account's open application. Re-POSTing while
 * `pending` REPLACES the open row (same id, new repo/evidence), which is what
 * lets one form double as "edit my application".
 *
 * Throws `ApiError` on non-2xx (400 on a rejected payload, 409 when the account
 * already holds an approved application); the caller surfaces it.
 */
export async function submitOssVerification(
  draft: OssVerificationDraft,
  token: string | null = SESSION_TOKEN,
): Promise<OssVerificationRequest> {
  const { request } = await apiFetch<{ request: OssVerificationRequest }>(
    '/v1/oss/verification',
    token,
    {
      method: 'POST',
      body: JSON.stringify({
        repo_full_name: draft.repo_full_name.trim(),
        evidence: draft.evidence.trim(),
      }),
    },
  );
  return request;
}
